import { resolveNutritionPlanActionSnapshot } from "./nutritionPlanActionSnapshot";
import { resolveNutritionPlanName } from "./nutritionPlanNameUtils";

export function getMealCount(record) {
  const directMealCount = Number(record?.meal_count);
  if (Number.isFinite(directMealCount) && directMealCount > 0) {
    return directMealCount;
  }

  if (Array.isArray(record?.meals)) {
    return record.meals.length;
  }

  return 0;
}

function getPlanDraftSnapshot(planLike) {
  if (planLike && typeof planLike?.getDraftSnapshot === "function") {
    return planLike.getDraftSnapshot();
  }

  return null;
}

function getFirstTextValue(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return "";
}

function getFirstObjectValue(...values) {
  for (const value of values) {
    if (value && typeof value === "object") {
      return value;
    }
  }

  return null;
}

function getFirstDefinedValue(...values) {
  for (const value of values) {
    if (value !== undefined) {
      return value;
    }
  }

  return undefined;
}

export function buildRenderablePlan(planLike, mealRecords = null, fallbackPlan = null) {
  const draftSnapshot = getPlanDraftSnapshot(planLike);
  const runtimePlan = planLike && typeof planLike === "object" ? planLike : {};
  const fallback = fallbackPlan && typeof fallbackPlan === "object" ? fallbackPlan : {};
  const resolvedMeals = Array.isArray(mealRecords)
    ? mealRecords
    : Array.isArray(draftSnapshot?.meals)
      ? draftSnapshot.meals
      : Array.isArray(runtimePlan?.meals)
        ? runtimePlan.meals
        : Array.isArray(fallback?.meals)
          ? fallback.meals
          : [];
  const resolvedName = resolveNutritionPlanName(draftSnapshot, runtimePlan, fallback);

  return {
    ...fallback,
    ...runtimePlan,
    ...(draftSnapshot ?? {}),
    id: getFirstTextValue(draftSnapshot?.id, runtimePlan?.id, fallback?.id),
    name: resolvedName,
    notes: getFirstTextValue(draftSnapshot?.notes, runtimePlan?.notes, fallback?.notes),
    goals: getFirstObjectValue(draftSnapshot?.goals, runtimePlan?.goals, fallback?.goals),
    createdAt: getFirstDefinedValue(
      draftSnapshot?.createdAt,
      runtimePlan?.createdAt,
      fallback?.createdAt,
      null,
    ),
    updatedAt: getFirstDefinedValue(
      draftSnapshot?.updatedAt,
      runtimePlan?.updatedAt,
      fallback?.updatedAt,
      null,
    ),
    meal_count:
      getMealCount({ meal_count: draftSnapshot?.meal_count }) ||
      getMealCount({ meal_count: runtimePlan?.meal_count }) ||
      getMealCount({ meal_count: fallback?.meal_count }) ||
      resolvedMeals.length,
    meals: resolvedMeals,
  };
}

export function hasTrustedAssignmentPlanMeals(planLike) {
  return planLike?.mealsHydrated === true;
}

export async function resolveAssignmentConflictPlanRecord({
  plan,
  nutritionStore,
  language = "en",
} = {}) {
  const normalizedPlanId = String(plan?.id ?? "").trim().toLowerCase();
  const sourceRootRecord = normalizedPlanId
    ? nutritionStore?.planRootRecordsById?.get?.(normalizedPlanId) ?? null
    : null;
  const loadMealRecords =
    typeof nutritionStore?.loadPlanMealRecords === "function"
      ? (resolvedPlanId, options = {}) =>
          nutritionStore.loadPlanMealRecords(resolvedPlanId, options)
      : undefined;
  const { planRecord, sourceKind } = await resolveNutritionPlanActionSnapshot({
    planId: normalizedPlanId || plan?.id,
    sourceEntity: plan,
    sourceRootRecord,
    fallbackPlan: plan,
    loadMealRecords,
    language,
  });

  return {
    planRecord,
    sourceKind,
  };
}

export function buildAssignmentPreviewRecord(planLike, now = new Date()) {
  return {
    client_plan_id: `preview:${String(planLike?.id ?? planLike?.name ?? "plan")}`,
    name: String(planLike?.name ?? "").trim(),
    createdAt: planLike?.createdAt ?? null,
    deployedAt: now,
    meal_count: getMealCount(planLike),
    isAssignmentPreview: true,
  };
}
